import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../apiClient';
import { useAuth } from '../auth/AuthProvider';
import { getMyVault, listRecipients, listMessages, listActiveReleases, createVault as apiCreateVault, type VaultDashboard, type MemberFirmBranding, type RecipientListItem, type MessageListItem, type ActiveRelease } from './vaultApi';

export type VaultStatus = 'loading' | 'ready' | 'needs-vault' | 'pending-attestation' | 'error';

export interface VaultDashboardState {
  status: VaultStatus;
  vault: VaultDashboard['vault'] | null;
  aggregates: VaultDashboard['aggregates'] | null;
  recipients: RecipientListItem[];
  messages: MessageListItem[];
  /** How many still-live (not withdrawn/delivered? — all non-deleted) messages each recipient has. */
  messageCountByRecipient: Record<string, number>;
  /** Emergency releases in flight on this vault that the owner can STOP. */
  activeReleases: ActiveRelease[];
  /** The firm that provides this member's vault, for co-branding. Null if none. */
  firm: MemberFirmBranding | null;
  error: string | null;
  createVault(): Promise<void>;
  reload(): Promise<void>;
}

export function useVaultDashboard(): VaultDashboardState {
  const { api } = useAuth();
  const [status, setStatus] = useState<VaultStatus>('loading');
  const [vault, setVault] = useState<VaultDashboard['vault'] | null>(null);
  const [aggregates, setAggregates] = useState<VaultDashboard['aggregates'] | null>(null);
  const [recipients, setRecipients] = useState<RecipientListItem[]>([]);
  const [messages, setMessages] = useState<MessageListItem[]>([]);
  const [activeReleases, setActiveReleases] = useState<ActiveRelease[]>([]);
  const [firm, setFirm] = useState<MemberFirmBranding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);

    // Step 1 — load the vault. ONLY a 404 here means "no vault yet"; any other
    // failure is a genuine error. Recipient-load failures must NOT reach this
    // branch (a listRecipients 404/500 is not "you have no vault").
    let dash: VaultDashboard;
    try {
      dash = await getMyVault(api);
    } catch (e) {
      if (!mounted.current) return;
      if (e instanceof ApiError && e.status === 404) { setStatus('needs-vault'); return; }
      setError(e instanceof Error ? e.message : 'Could not load your vault.');
      setStatus('error');
      return;
    }

    // Step 2 — the vault exists; load its recipients + messages together. Any
    // failure here is an error state, never 'needs-vault'.
    try {
      const [list, msgs, releases] = await Promise.all([
        listRecipients(api, dash.vault.id),
        listMessages(api, dash.vault.id),
        // A release in flight is urgent but must never block the dashboard from
        // loading — if this call fails, show the vault, just without the banner.
        listActiveReleases(api, dash.vault.id).catch(() => ({ releases: [] })),
      ]);
      if (!mounted.current) return;
      setVault(dash.vault);
      setAggregates(dash.aggregates);
      setFirm(dash.firm ?? null);
      setRecipients(list.recipients);
      setMessages(msgs.messages);
      setActiveReleases(releases.releases);
      setStatus('ready');
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : 'Could not load your recipients.');
      setStatus('error');
    }
  }, [api]);

  const createVault = useCallback(async () => {
    setError(null);
    try {
      await apiCreateVault(api);
      await reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) { await reload(); return; }
      if (!mounted.current) return;
      if (e instanceof ApiError && e.status === 403) { setStatus('pending-attestation'); return; }
      setError(e instanceof Error ? e.message : 'Could not create your vault.');
      setStatus('error');
    }
  }, [api, reload]);

  useEffect(() => {
    mounted.current = true;
    void reload();
    return () => { mounted.current = false; };
  }, [reload]);

  const messageCountByRecipient: Record<string, number> = {};
  for (const m of messages) {
    // A withdrawn message is no longer a live promise; don't count it against a recipient.
    if (m.status === 'withdrawn' || m.recipient_id === null) continue;
    messageCountByRecipient[m.recipient_id] = (messageCountByRecipient[m.recipient_id] ?? 0) + 1;
  }

  return { status, vault, aggregates, recipients, messages, messageCountByRecipient, activeReleases, firm, error, createVault, reload };
}
